import React, { useEffect } from "react";
import PropTypes from "prop-types";
import "../Job/Job.scss";
import SoftButton from "StyleFeatures/SoftButton";
import jobInviteService from "../APIServices/JobInviteService";
import { ToastContainer, toast } from "react-toastify";
import Footer from "Common/Footer";
import { Card, Select, DatePicker, Tooltip } from "antd";
import moment from "moment";

import {
  DesignationLevelEnum,
  DesignaitonEnumLabel,
} from "Constants/Enums/DesignationLevelEnum";
import { EmploymentTypeOptions } from "Constants/Enums/EmploymentTypeEnum";
import {
  WorkAuthorizationStatusEnum,
  WorkAuthorizationApplyOptions,
} from "Constants/Enums/WorkAuthorizationStatusEnum";

const { Option } = Select;
const { RangePicker } = DatePicker;

function JobS({ onBack, onNext, initialData = {}, jobData = {} }) {
  const {
    jobFormData,
    setJobFormData,
    jobFormDataError,
    handleJobChange,
    handleDesignationLevelChange,
    validateStep2,
    designations,
    departments,
    jobTitles,
    disableProbationStart,
    disableProbationEnd,
    isExecutive
  } = jobInviteService();

  // Initialize form with initialData when component mounts (for navigating back)
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      setJobFormData((prev) => ({
        ...prev,
        ...initialData,
      }));
    }
  }, [initialData, setJobFormData]);

  // Update departmentId from jobData
  useEffect(() => {
    if (jobData?.departmentId) {
      setJobFormData((prev) => ({
        ...prev,
        departmentId: jobData.departmentId,
      }));
    }
  }, [jobData, setJobFormData]);

  // Initialize designation level
  useEffect(() => {
    setJobFormData((prev) => ({
      ...prev,
      designationLevel: prev.designationLevel ?? DesignationLevelEnum.Executive,
    }));
  }, []);

  // Handle date changes
  const handleDateChange = (field, date, dateString) => {
    setJobFormData((prev) => ({
      ...prev,
      [field]: dateString,
    }));
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    const isValid = validateStep2();
    if (!isValid) {
      toast.error("Please fill all required fields correctly");
      return;
    }

    const selectedDesignation = designations.find(
      (d) => d.id === jobFormData.designationId
    );

    // Hiring baseline (Mudassir Jun-16): invited employees don't come from an
    // application, so employment type + work authorization are required here.
    if (
      jobFormData.employmentType === undefined ||
      jobFormData.employmentType === "" ||
      jobFormData.workAuthorizationStatus === undefined ||
      jobFormData.workAuthorizationStatus === ""
    ) {
      toast.error("Please select employment type and work authorization status");
      return;
    }
    const isCitizenSel =
      Number(jobFormData.workAuthorizationStatus) === WorkAuthorizationStatusEnum.USCitizen;
    if (
      !isCitizenSel &&
      jobFormData.sponsorshipNeeded !== true &&
      jobFormData.sponsorshipNeeded !== false
    ) {
      toast.error("Please answer the sponsorship question");
      return;
    }

    const dataForParent = {
      hiringDate: jobFormData.hiringDate,
      probationStartDate: jobFormData.probationStartDate,
      probationEndDate: jobFormData.probationEndDate,
      payType: Number(jobFormData.payType || 0),
      baseSalary: Number(jobFormData.baseSalary),
      priorExperience: Number(jobFormData.priorExperience || 0),
      designationId: Number(jobFormData.designationId),
      departmentId: isExecutive() ? null : Number(jobFormData.departmentId),
      jobTitleId: isExecutive() ? null : Number(jobFormData.jobTitleId),
      designationName: selectedDesignation?.name || "",
      designationRank: selectedDesignation?.rank || 0,
      employmentType: Number(jobFormData.employmentType),
      workAuthorizationStatus: Number(jobFormData.workAuthorizationStatus),
      sponsorshipNeeded: isCitizenSel ? false : jobFormData.sponsorshipNeeded === true,
    };

    onNext(dataForParent);
  };

  // Handle back navigation
  const handleBack = () => {
    onBack();
  };

  return (
    <div className="body flex-grow-1 px-3">
      <Card title="Job" className="card-professional">
        <ToastContainer />

        <form>
          <div className="row g-3 align-items-end">
            {/* DESIGNATION LEVEL */}
            <div className="col-md-4">
              <label className="form-label">Designation Level *</label>
              <Select
                className="w-100"
                placeholder="Select Designation Level"
                value={jobFormData.designationLevel || undefined}
                onChange={(val) => {
                  handleDesignationLevelChange(val);
                }}
              >
                {Object.values(DesignationLevelEnum).map((val) => (
                  <Option key={val} value={val}>
                    {DesignaitonEnumLabel[val]}
                  </Option>
                ))}
              </Select>
              {jobFormDataError?.designationLevelError && (
                <div className="text-danger mt-1" style={{ fontSize: "13px" }}>
                  {jobFormDataError.designationLevelError}
                </div>
              )}
            </div>

            {/* DESIGNATION */}
            <div className="col-md-4">
              <label className="form-label">Designation *</label>
              <Select
  className="w-100"
  value={jobFormData.designationId || undefined}
  onChange={(val) =>
    handleJobChange({
      target: { name: "designationId", value: val },
    })
  }
>
                <option value="">Select Designation</option>
              {designations?.map((d) => (
  <Select.Option
    key={d.id}
    value={d.id}
    disabled={d.isSingleSeat && d.employeeCount > 0}
  >
    <Tooltip
      title={
        d.isSingleSeat && d.employeeCount > 0
          ? "Single seat already filled"
          : ""
      }
    >
      <span>{d.name}</span>
    </Tooltip>
  </Select.Option>
))}
              </Select>
              {jobFormDataError?.designationIdError && (
                <div className="invalid-feedback">{jobFormDataError.designationIdError}</div>
              )}
            </div>

                        {/* Prior Experience */}
            <div className="col-md-4">
              <label className="form-label">Prior Experience (years) *</label>
              <input
                type="number"
                step="0.5"
                min="0"
                max="60"
                name="priorExperience"
                value={jobFormData.priorExperience || ""}
                onChange={handleJobChange}
                className={`form-control ${jobFormDataError?.priorExperienceError ? "is-invalid" : ""}`}
                placeholder="e.g. 3 (years)"
              />
              {jobFormDataError?.probationEndDateError && (
                <div className="invalid-feedback">{jobFormDataError.priorExperienceError}</div>
              )}
            </div>

            {/* BASE SALARY */}
            <div className="col-md-4">
              <label className="form-label">Base Salary (annual) *</label>
              <input
                type="number"
                step="1000"
                min="0"
                name="baseSalary"
                value={jobFormData.baseSalary || ""}
                onChange={handleJobChange}
                className={`form-control ${jobFormDataError?.baseSalaryError ? "is-invalid" : ""}`}
                placeholder="e.g. 60000"
              />
              {jobFormDataError?.baseSalaryError && (
                <div className="invalid-feedback">{jobFormDataError.baseSalaryError}</div>
              )}
            </div>
          </div>

          <div className="row g-3 mt-3">
        
            {/* HIRING DATE */}
            <div className="col-md-4">
              <label className="form-label">Hiring Date *</label>
              <DatePicker
                className="w-100"
                value={jobFormData.hiringDate ? moment(jobFormData.hiringDate) : null}
                onChange={(date, dateString) => handleDateChange("hiringDate", date, dateString)}
                disabledDate={(current) => current && current < moment().startOf("day")}
              />
              {jobFormDataError?.hiringDateError && (
                <div className="text-danger mt-1" style={{ fontSize: "13px" }}>
                  {jobFormDataError.hiringDateError}
                </div>
              )}
            </div>

            {/* PROBATION START DATE */}
            <div className="col-md-4">
              <label className="form-label">Probation Start Date *</label>
              <DatePicker
                className="w-100"
                value={jobFormData.probationStartDate ? moment(jobFormData.probationStartDate) : null}
                onChange={(date, dateString) => handleDateChange("probationStartDate", date, dateString)}
                disabledDate={disableProbationStart}
              />
              {jobFormDataError?.probationStartDateError && (
                <div className="text-danger mt-1" style={{ fontSize: "13px" }}>
                  {jobFormDataError.probationStartDateError}
                </div>
              )}
            </div>

             {/* PROBATION END DATE */}
            <div className="col-md-4">
              <label className="form-label">Probation End Date *</label>
              <DatePicker
                className="w-100"
                value={jobFormData.probationEndDate ? moment(jobFormData.probationEndDate) : null}
                onChange={(date, dateString) => handleDateChange("probationEndDate", date, dateString)}
                disabledDate={disableProbationEnd}
              />
              {jobFormDataError?.probationEndDateError && (
                <div className="text-danger mt-1" style={{ fontSize: "13px" }}>
                  {jobFormDataError.probationEndDateError}
                </div>
              )}
            </div>
          </div>

          <div className="row g-3 mt-3">
           

            {/* DEPARTMENT - Only show for non-executive */}
            {!isExecutive() && (
              <div className="col-md-4">
                <label className="form-label">Department *</label>
                <select
                  name="departmentId"
                  value={jobFormData.departmentId || ""}
                  onChange={handleJobChange}
                  className={`form-control ${jobFormDataError?.departmentIdError ? "is-invalid" : ""}`}
                >
                  <option value="">Select Department</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
                {jobFormDataError?.departmentIdError && (
                  <div className="invalid-feedback">{jobFormDataError.departmentIdError}</div>
                )}
              </div>
            )}

            {/* JOB TITLE - Only show for non-executive */}
            {!isExecutive() && !!jobFormData.departmentId && (
              <div className="col-md-4">
                <label className="form-label">Job Title *</label>
                <select
                  name="jobTitleId"
                  value={jobFormData.jobTitleId || ""}
                  onChange={handleJobChange}
                  className={`form-control ${jobFormDataError?.jobTitleIdError ? "is-invalid" : ""}`}
                >
                  <option value="">Select Job Title</option>
                  {jobTitles.map((jt) => (
                    <option key={jt.id} value={jt.id}>
                      {jt.name}
                    </option>
                  ))}
                </select>
                {jobFormDataError?.jobTitleIdError && (
                  <div className="invalid-feedback">{jobFormDataError.jobTitleIdError}</div>
                )}
              </div>
            )}
          </div>


          {/* HIRING BASELINE: employment type + work authorization */}
          <div className="row g-3 mt-3">
            <div className="col-md-4">
              <label className="form-label">Employment Type *</label>
              <Select
                className="w-100"
                placeholder="Select Employment Type"
                value={jobFormData.employmentType ?? undefined}
                onChange={(val) =>
                  handleJobChange({ target: { name: "employmentType", value: val } })
                }
              >
                {EmploymentTypeOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Select>
            </div>

            <div className="col-md-4">
              <label className="form-label">Work Authorization Status *</label>
              <Select
                className="w-100"
                placeholder="Select Work Authorization"
                value={jobFormData.workAuthorizationStatus ?? undefined}
                onChange={(val) => {
                  const isCitizen = Number(val) === WorkAuthorizationStatusEnum.USCitizen;
                  setJobFormData((prev) => ({
                    ...prev,
                    workAuthorizationStatus: val,
                    sponsorshipNeeded: isCitizen ? false : prev.sponsorshipNeeded,
                  }));
                }}
              >
                {WorkAuthorizationApplyOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Select>
            </div>

            {jobFormData.workAuthorizationStatus !== undefined &&
              jobFormData.workAuthorizationStatus !== "" &&
              Number(jobFormData.workAuthorizationStatus) !==
                WorkAuthorizationStatusEnum.USCitizen && (
                <div className="col-md-4">
                  <label className="form-label">Sponsorship Needed *</label>
                  <Select
                    className="w-100"
                    placeholder="Select"
                    value={
                      jobFormData.sponsorshipNeeded === true
                        ? "true"
                        : jobFormData.sponsorshipNeeded === false
                        ? "false"
                        : undefined
                    }
                    onChange={(val) =>
                      setJobFormData((prev) => ({
                        ...prev,
                        sponsorshipNeeded: val === "true",
                      }))
                    }
                  >
                    <Option value="true">Yes</Option>
                    <Option value="false">No</Option>
                  </Select>
                </div>
              )}
          </div>

          {/* BUTTONS */}
          <div className="mt-4 text-end">
            <SoftButton
              style={{ marginRight: "8px" }}
              onClick={handleBack}
              className="btn-back"
            >
              Back
            </SoftButton>

            <SoftButton
              onClick={handleSubmit}
              className="btn-primary"
            >
              Next
            </SoftButton>
          </div>
        </form>
      </Card>

      <Footer />
    </div>
  );
}

JobS.propTypes = {
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  initialData: PropTypes.object,
  jobData: PropTypes.object,
};

JobS.defaultProps = {
  initialData: {},
  jobData: {},
};

export default JobS;